import { Injectable, isDevMode, inject } from '@angular/core';
import { settingsSelectors, User, UserRole, UserType } from '@wam/shared';
import { select } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApiGatewayService, authenticationSelectors, JwtService } from '@wam/authentication';
import { Organization } from '../organizations/organization.model';
import { isNil } from 'lodash-es';
import { WithAuthService } from '../shared/services/with-auth.service';
import * as authenticationActions from '@app/authentication/state/authentication.actions';

export class UnknownUserTypeError extends Error {
  constructor() {
    super('AuthenticationService: Unknown user type during Cognito settings initialization.');
  }
}

export class FusionAuthIncompatibilityError extends Error {
  constructor() {
    super('AuthenticationService: User type incompatible with Fusion authentication.');
  }
}

enum ProviderNameMap {
  SignInWithApple = 'Apple',
  Google = 'Google',
  Waterford = 'Waterford',
}

type AuthAction = 'login' | 'signup' | 'logout';

const COGNITO_ISSUER_PATTERN = /^https:\/\/cognito-idp\.[a-z0-9-]+\.amazonaws\.com\/[^/]+$/;

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService extends WithAuthService {
  private http = inject(HttpClient);
  private apiGatewayService = inject(ApiGatewayService);
  private jwtHelperService = inject(JwtService);
  // @ts-ignore
  private window = inject<Window>('window');

  logIn() {
    return this.redirectTo('login');
  }

  signUp() {
    return this.redirectTo('signup');
  }

  logOut() {
    return this.redirectTo('logout');
  }

  buildUser(idToken: string, refreshToken: string | undefined, userType: UserType): User {
    const decodedJwt = this.jwtHelperService.decodeToken(idToken);

    const providerName = decodedJwt['identities']?.[0]?.['providerName'] || 'Waterford';

    const roles = this.decodeRoles(decodedJwt);

    return {
      idToken,
      refreshToken,
      name: decodedJwt['name'],
      preferredName: decodedJwt['preferredName'],
      cognitoUsername: decodedJwt['cognito:username'],
      type: userType,
      roles,
      primaryRole: this.decodePrimaryRole(decodedJwt, roles),
      application: this.getApplication(userType),
      uuid: decodedJwt['custom:uuid'],
      sub: decodedJwt['sub'],
      organization: decodedJwt['custom:organization'],
      grade: parseInt(decodedJwt['custom:grade'], 10) - 2,
      school: decodedJwt['custom:school'],
      classroom: decodedJwt['custom:class'],
      email: decodedJwt['email'],
      providerName: ProviderNameMap[providerName],
    };
  }

  refreshToken(): Observable<User> {
    return combineLatest([
      this.store.pipe(
        select(authenticationSelectors.getRefreshToken),
        filter((token) => !isNil(localStorage.getItem('documentId')) || !isNil(token)),
      ),
      this.getSettings(),
    ]).pipe(
      switchMap(([refreshToken, { url, clientId }]) =>
        this.refreshFusionToken(url, clientId, refreshToken),
      ),
      mergeMap((token) => {
        return this.store.pipe(
          select(settingsSelectors.getUserType),
          withLatestFrom(this.store.select(authenticationSelectors.getRefreshToken)),
          map(([userType, refreshToken]) => this.buildUser(token, refreshToken, userType)),
        );
      }),
      take(1),
    );
  }

  getReadOnly(organization: string, _type?: UserType): Observable<boolean> {
    if (isNil(organization)) {
      return of(true);
    }

    return this.apiGatewayService
      .simpleGet<{ org: Organization }>(`rostering/v2/rostering/source/${organization}`)
      .pipe(map(({ org }) => org.readOnly));
  }

  getOrgCode(organization?: string): Observable<string> {
    return this.withUser().pipe(
      mergeMap((user) =>
        this.apiGatewayService.get<{ orgCode: string }>(
          `user-lookup/v2/auth/tenants/${organization ?? user.organization}/codes`,
        ),
      ),
      map((response) => response.orgCode),
    );
  }

  refreshFusionToken(
    url: string,
    clientId: string,
    refreshToken: string | undefined,
  ): Observable<string> {
    return this.store.pipe(
      select(settingsSelectors.getApiSettings),
      map(({ key }) => key),
      mergeMap((key) =>
        this.http.get<{ data: { attributes: { idToken: string } } }>(
          `${url}/clients/${clientId}/external/refresh/${refreshToken}`,
          { headers: { 'X-Api-Key': key } },
        ),
      ),
      map((response) => response.data.attributes.idToken),
      take(1),
    );
  }

  getSettings(): Observable<{
    url: string;
    clientId: string;
  }> {
    return this.getFusionSettings();
  }

  getFusionSettings(): Observable<{
    url: string;
    clientId: string;
  }> {
    return combineLatest([
      this.store.select(settingsSelectors.getFusionSettings),
      this.store.select(authenticationSelectors.getClientId),
    ]).pipe(map(([{ userLookupUrl }, clientId]) => ({ url: userLookupUrl, clientId })));
  }

  getFusionUrl(action: AuthAction = 'login'): Observable<string> {
    return combineLatest([
      this.store.select(settingsSelectors.getFusionSettings),
      this.store.select(settingsSelectors.getUserType),
      this.store.select(authenticationSelectors.getCurrentUser),
      this.store.select(authenticationSelectors.getClientId),
    ]).pipe(
      // @ts-ignore
      map(([{ loginUrl, adminLoginUrl, userLookupUrl }, userType, user, clientId]) => {
        switch (userType) {
          case UserType.TEACHER:
          case UserType.ADMIN:
            if (action === 'login' || action === 'signup') {
              return this.getLoginUrl(userType, adminLoginUrl, loginUrl);
            }
            if (action === 'logout') {
              return this.getLogoutUrl(clientId, user, userLookupUrl);
            }
            break;
          default:
            throw new FusionAuthIncompatibilityError();
        }
      }),
      take(1),
    );
  }

  private getLoginUrl(userType: UserType, adminLoginUrl: string, loginUrl: string): string {
    const userLoginUrl = userType === UserType.ADMIN ? adminLoginUrl : loginUrl;
    let target;

    if (/localhost/.test(this.window.location.href)) {
      target = 'local';
    }

    const pullRequestMatch = /\/\/(.*pr-\d+)/.exec(this.window.location.href) ?? [];

    if (pullRequestMatch[1]) {
      target = pullRequestMatch[1];
    }

    const targetUrl = target ? `/?login=new&target=${target}` : '';
    return `${userLoginUrl}${targetUrl}`;
  }

  private getLogoutUrl(clientId: string, user: User, userLookupUrl): string {
    return `${userLookupUrl}/orgs/${clientId}/logout/${user?.idToken}`;
  }

  getApplication(userType: UserType): string {
    return `waterford-${userType.toString().toLowerCase()}`;
  }

  redirectUri(devMode = isDevMode()): string {
    return `${devMode ? 'http' : 'https'}://${this.window.location.host}/logged-in`;
  }

  redirectTo(place: AuthAction) {
    return combineLatest(this.store.select(authenticationSelectors.getLogoutUrl)).pipe(
      take(1),
      mergeMap(([logoutUrl]) => {
        if (place === 'logout' && !isNil(logoutUrl)) {
          return this.store.pipe(
            select(authenticationSelectors.getIdToken),
            withLatestFrom(this.store.select(authenticationSelectors.includeLogoutToken)),
            map(([idToken, includeLogoutToken]) =>
              includeLogoutToken ? `${logoutUrl}?idToken=${idToken}` : logoutUrl,
            ),
            take(1),
          );
        }
        return this.getFusionUrl(place);
      }),
      tap((url) => {
        this.window.location.href = url;
      }),
      map(() => authenticationActions.logInActionSuccess()),
    );
  }

  // Platform services normalize `district_admin`-style values and treat the first role as primary.
  private decodePrimaryRole(decodedJwt, roles: UserRole[]): UserRole | undefined {
    const primaryRole = decodedJwt['primaryRole'] ?? roles[0];
    return typeof primaryRole === 'string'
      ? (primaryRole.replaceAll('_', '-') as UserRole)
      : undefined;
  }

  private decodeRoles(decodedJwt): UserRole[] {
    if (decodedJwt['roles']) {
      return decodedJwt['roles'];
    }

    // Every Cognito-issued token without a `roles` claim belongs to an external parent. These
    // tokens have no `custom:organization` claim either (handled separately wherever
    // `User.organization` feeds an API path - see `OrganizationsDataService.getAcademicSession()`
    // and `ReportsService.getReports()`).
    if (this.isCognitoIssued(decodedJwt)) {
      return [UserRole.EXT_PARENT];
    }

    return [];
  }

  private isCognitoIssued(decodedJwt): boolean {
    const issuer = decodedJwt['iss'];
    return typeof issuer === 'string' && COGNITO_ISSUER_PATTERN.test(issuer);
  }
}
